import { useEffect, useState } from "react";

/**
 * Deterrents against casual screen-capture and DevTools use - right-click,
 * the usual DevTools/view-source shortcuts, and a size-heuristic that
 * catches a docked DevTools panel.
 *
 * None of this is a real security boundary and it cannot be one: the
 * operating system's own screenshot tool, a phone camera, a screen recorder,
 * or a browser extension all operate completely outside a web page's
 * control, and a technical visitor can always reach DevTools anyway (a
 * detached window, remote debugging, disabling JavaScript first, or simply
 * viewing source over the network). What this raises is the bar for casual
 * attempts - it is not a guarantee, and nothing served to a browser ever can
 * be.
 */
export default function useAntiInspect() {
  const [devtoolsOpen, setDevtoolsOpen] = useState(false);

  useEffect(() => {
    const onContextMenu = (e) => e.preventDefault();

    const BLOCKED_KEYS = new Set(["F12"]);
    function onKeyDown(e) {
      const key = e.key?.toUpperCase();
      const combo = (e.ctrlKey || e.metaKey) && e.shiftKey && ["I", "J", "C"].includes(key);
      const viewSource = (e.ctrlKey || e.metaKey) && key === "U";
      if (BLOCKED_KEYS.has(e.key) || combo || viewSource) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, []);

  useEffect(() => {
    // A docked DevTools panel shrinks the viewport relative to the outer
    // window; an undocked or remote one does not, which is exactly why this
    // is a heuristic and not a detector - it has both false negatives and,
    // on an oddly-sized real window, occasional false positives.
    const THRESHOLD = 160;
    function check() {
      const widthGap = window.outerWidth - window.innerWidth;
      const heightGap = window.outerHeight - window.innerHeight;
      setDevtoolsOpen(widthGap > THRESHOLD || heightGap > THRESHOLD);
    }
    check();
    const t = setInterval(check, 1000);
    window.addEventListener("resize", check);
    return () => {
      clearInterval(t);
      window.removeEventListener("resize", check);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("devtools-open", devtoolsOpen);
  }, [devtoolsOpen]);

  return devtoolsOpen;
}
